import { Box, Flex, Icon, Skeleton, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiCheck } from "react-icons/fi";
import { prefersReducedMotion } from "../../../../common/scroll";
import type { QualificationStep } from "./deriveQualificationSteps";

// Dynamic keys from deriveQualificationSteps — listed here so langen picks them up.
// t('Review the requests') t('Choose who to invite') t('Send the invitation')
// t('{{reviewed}} of {{total}} reviewed') t('Waiting for the reviews')
// t('{{count}} reasons missing', {count: 1}) t('Too many selected') t('Too few selected')
// t('The bid deadline is set here') t('{{selected}} of {{qualified}} qualified')
// t('Complete the selection above before sending.')

interface Props {
  steps: QualificationStep[];
  sectionIds: { review: string; select?: string; send: string };
  isLoading?: boolean;
}

const STEP_NUMBER: Record<QualificationStep["key"], number> = {
  review: 1,
  select: 2,
  send: 3,
};

// Scroll to a section and move focus to its heading.
const scrollToSection = (sectionId: string) => {
  const el = document.getElementById(sectionId);
  if (!el) return;
  el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth" });
  const heading = el.querySelector<HTMLElement>("h2, h3, h4, [tabindex]");
  heading?.focus();
};

export const QualificationStepper = ({ steps, sectionIds, isLoading }: Props) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Flex
        as="nav"
        aria-label={String(t("Qualification steps"))}
        gap="4"
        direction={{ base: "column", md: "row" }}
        mb="5"
      >
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height="48px" flex="1" rounded="lg" />
        ))}
      </Flex>
    );
  }

  if (steps.length === 0) return null;

  const sectionIdFor = (key: QualificationStep["key"]): string | undefined => sectionIds[key];

  return (
    <Flex
      as="nav"
      aria-label={String(t("Qualification steps"))}
      gap={{ base: "2", md: "4" }}
      direction={{ base: "column", md: "row" }}
      mb="5"
    >
      {steps.map((step) => {
        const isDone = step.state === "done";
        const isActive = step.state === "active";
        const isWaiting = step.state === "waiting";
        const stepNum = STEP_NUMBER[step.key];
        const sectionId = sectionIdFor(step.key);

        return (
          <Box
            key={step.key}
            as="button"
            flex="1"
            textAlign="left"
            aria-current={isActive ? "step" : undefined}
            onClick={() => sectionId && scrollToSection(sectionId)}
            border="1px solid"
            borderColor={isActive ? "smPrimary" : "smBorder"}
            bg="smBackground"
            rounded="lg"
            px="4"
            py="3"
            cursor="pointer"
            _hover={{ bg: "smBackgroundSecondary" }}
            transition="background 0.1s"
          >
            <Flex align="center" gap="3">
              {/* Step indicator circle */}
              <Flex
                align="center"
                justify="center"
                w="7"
                h="7"
                rounded="full"
                flexShrink={0}
                bg={isDone ? "smPrimary" : isActive ? "smPrimary" : "smBorder"}
                color={isDone || isActive ? "white" : "smMuted"}
                fontSize="xs"
                fontWeight="bold"
              >
                {isDone ? <Icon as={FiCheck} boxSize="4" /> : stepNum}
              </Flex>

              <Box>
                <Text
                  fontSize="sm"
                  fontWeight={isActive ? "semibold" : "medium"}
                  color={isWaiting ? "smMuted" : "smText"}
                >
                  {t(step.titleKey)}
                </Text>
                {step.subtitle && (
                  <Text fontSize="xs" color="smMuted" mt="0.5">
                    {step.subtitle.params
                      ? t(step.subtitle.key, step.subtitle.params)
                      : t(step.subtitle.key)}
                  </Text>
                )}
              </Box>
            </Flex>
          </Box>
        );
      })}
    </Flex>
  );
};
